/**
 * PhotoUploadForm component - Modal for uploading photos (now supports multiple photos as stories)
 */

import { useState, useRef } from 'react';
import { Upload, X, Loader2, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { useUploadStory } from '../../services/stories';
import { cn } from '../../lib/utils';

interface PhotoUploadFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_VIDEO_DURATION = 60; // 60 seconds
const MAX_ITEMS = 10;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
// Accept all common video MIME types - server will handle transcoding if needed
const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/x-matroska', // MKV
  'video/mpeg',
  'video/webm',
  '', // Some devices don't set MIME type - we'll check extension
];

interface MediaPreview {
  file: File;
  preview: string;
  id: string;
  mediaType: 'image' | 'video';
  duration?: number; // For videos
}

export const PhotoUploadForm = ({ open, onOpenChange }: PhotoUploadFormProps) => {
  const [selectedPhotos, setSelectedPhotos] = useState<MediaPreview[]>([]);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadStory();

  // Helper function to get video duration
  const getVideoDuration = (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      const cleanup = () => {
        if (video.src) {
          URL.revokeObjectURL(video.src);
        }
      };

      // Add timeout in case metadata never loads
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Timeout loading video metadata (10s)'));
      }, 10000); // 10 second timeout

      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        const duration = video.duration;
        cleanup();

        // Check if duration is valid
        if (!duration || !isFinite(duration) || duration <= 0) {
          reject(new Error('Video duration could not be determined'));
          return;
        }

        resolve(duration);
      };

      video.onerror = (e) => {
        clearTimeout(timeout);
        cleanup();
        console.error(
          'Video metadata loading error:',
          e,
          'File:',
          file.name,
          'Type:',
          file.type,
          'Size:',
          file.size
        );
        reject(new Error(`Failed to load video metadata. File type: ${file.type}`));
      };

      try {
        video.src = URL.createObjectURL(file);
        // Force load to start
        video.load();
      } catch (error) {
        clearTimeout(timeout);
        cleanup();
        reject(new Error(`Failed to create video URL: ${error}`));
      }
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check total count
    if (selectedPhotos.length + files.length > MAX_ITEMS) {
      setError(`Maximum ${MAX_ITEMS} items allowed per story`);
      return;
    }

    // Validate and create previews for each file
    const validMedia: MediaPreview[] = [];
    let hasError = false;

    // Process files sequentially to handle async video duration
    for (const file of files) {
      try {
        // Check by MIME type first, then fall back to extension if MIME type is missing/unknown
        let isVideo = ACCEPTED_VIDEO_TYPES.includes(file.type);
        let isImage = ACCEPTED_IMAGE_TYPES.includes(file.type);

        // If MIME type is not set or unknown, check file extension
        if (!isVideo && !isImage) {
          const extension = file.name.split('.').pop()?.toLowerCase() || '';
          const videoExtensions = ['mp4', 'mov', 'm4v'];
          const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

          if (videoExtensions.includes(extension)) {
            isVideo = true;
          } else if (imageExtensions.includes(extension)) {
            isImage = true;
          }
        }

        // Validate file type
        if (!isVideo && !isImage) {
          console.error('Invalid file type:', {
            name: file.name,
            type: file.type,
            size: file.size,
          });
          setError('Please select valid image (JPEG, PNG, WebP) or video (MP4, MOV) files');
          hasError = true;
          break;
        }

        // Validate file size
        const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
        if (file.size > maxSize) {
          setError(
            `${isVideo ? 'Videos' : 'Images'} must be less than ${isVideo ? '50MB' : '10MB'}`
          );
          hasError = true;
          break;
        }

        let duration: number | undefined;

        // Get video duration
        if (isVideo) {
          try {
            duration = await getVideoDuration(file);
            if (duration > MAX_VIDEO_DURATION) {
              setError(`Videos must be ${MAX_VIDEO_DURATION} seconds or less`);
              hasError = true;
              break;
            }
          } catch (err) {
            console.warn('Could not read video metadata (may be HEVC/H.265):', {
              name: file.name,
              type: file.type,
              size: file.size,
              error: err,
            });
            // For videos we can't read (like HEVC), estimate 30 seconds
            // Server will validate actual duration during processing
            duration = 30;
          }
        }

        // Create preview
        const preview = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });

        validMedia.push({
          file,
          preview,
          id: `${Date.now()}-${Math.random()}`,
          mediaType: isVideo ? 'video' : 'image',
          duration,
        });
      } catch (err) {
        setError('Failed to process file');
        hasError = true;
        break;
      }
    }

    // Update state if no errors
    if (!hasError && validMedia.length > 0) {
      setSelectedPhotos((prev) => [...prev, ...validMedia]);
      setError(null);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (id: string) => {
    setSelectedPhotos((prev) => prev.filter((photo) => photo.id !== id));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newPhotos = [...selectedPhotos];
    const draggedPhoto = newPhotos[draggedIndex];
    newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(index, 0, draggedPhoto);

    setSelectedPhotos(newPhotos);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedPhotos.length === 0) {
      setError('Please select at least one photo to upload');
      return;
    }

    try {
      const files = selectedPhotos.map((photo) => photo.file);
      await uploadMutation.mutateAsync({
        files,
        caption: caption.trim() || undefined,
      });

      // Success - reset form and close modal
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload photos');
    }
  };

  const handleClose = () => {
    setSelectedPhotos([]);
    setCaption('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden p-0 sm:max-w-[600px]">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Upload Photos</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Upload up to {MAX_ITEMS} photos or videos. Drag to reorder.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div
            className="flex-1 space-y-4 overflow-y-auto px-6"
            tabIndex={0}
            aria-label="Photo upload form content"
          >
            {/* File input area */}
            <div className="space-y-2">
              <Label>
                Media ({selectedPhotos.length}/{MAX_ITEMS})
              </Label>
              <input
                ref={fileInputRef}
                type="file"
                id="photos"
                accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp,video/mp4,video/quicktime,.mp4,.mov,.m4v"
                onChange={handleFileSelect}
                className="sr-only"
                multiple
                aria-hidden="true"
              />

              {/* Upload zone */}
              {selectedPhotos.length < MAX_ITEMS && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 text-center transition-colors hover:border-muted-foreground/50"
                >
                  <Upload className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="mb-2 text-sm text-muted-foreground">
                    Click to select photos or videos ({MAX_ITEMS - selectedPhotos.length} remaining)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Images: JPEG, PNG, WebP (Max 10MB) • Videos: MP4, MOV (Max 50MB, 60s)
                  </p>
                </div>
              )}

              {/* Media previews grid */}
              {selectedPhotos.length > 0 && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {selectedPhotos.map((media, index) => (
                    <div
                      key={media.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={cn(
                        'group relative overflow-hidden rounded-lg border border-border bg-muted',
                        'cursor-move transition-opacity',
                        draggedIndex === index && 'opacity-50'
                      )}
                    >
                      {/* Order badge */}
                      <div className="absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-xs font-semibold text-white">
                        {index + 1}
                      </div>

                      {/* Media type indicator */}
                      {media.mediaType === 'video' && (
                        <div className="absolute right-2 top-2 z-10 rounded bg-black/60 px-2 py-1 text-xs font-semibold text-white">
                          VIDEO
                        </div>
                      )}

                      {/* Drag handle */}
                      <div className="absolute bottom-2 left-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100">
                        <GripVertical className="h-4 w-4" />
                      </div>

                      {/* Preview image or video */}
                      {media.mediaType === 'video' ? (
                        <video
                          src={media.preview}
                          className="aspect-square w-full object-cover"
                          muted
                        />
                      ) : (
                        <img
                          src={media.preview}
                          alt={`Preview ${index + 1}`}
                          className="aspect-square w-full object-cover"
                        />
                      )}

                      {/* Video duration */}
                      {media.mediaType === 'video' && media.duration && (
                        <div className="absolute bottom-2 left-2 z-10 rounded bg-black/60 px-2 py-1 text-xs text-white">
                          {Math.floor(media.duration)}s
                        </div>
                      )}

                      {/* Remove button */}
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute bottom-2 right-2 z-10 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto(media.id);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Caption input */}
            <div className="space-y-2">
              <Label htmlFor="caption">Caption (optional)</Label>
              <Textarea
                id="caption"
                placeholder="Write a caption for your story..."
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={500}
                rows={3}
                disabled={uploadMutation.isPending}
              />
              <p className="text-right text-xs text-muted-foreground">{caption.length}/500</p>
            </div>

            {/* Error message */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Upload progress */}
            {uploadMutation.isPending && (
              <Alert>
                <AlertDescription className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading {selectedPhotos.length} photo{selectedPhotos.length !== 1 ? 's' : ''}...
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Action buttons */}
          <DialogFooter className="border-t px-6 pb-6 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={uploadMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={selectedPhotos.length === 0 || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                `Upload ${selectedPhotos.length} Photo${selectedPhotos.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
